import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, baseStyles } from '../styles';
import { SearchBar, CategoryButton, CollapsibleSection, AgregarLugarModal, LocationPrompt, LocationIndicator } from '../components';
import { categoriasMascota, categoriasObjeto } from '../data/fakeData';
import { resolveStoredLocation } from '../services/locationService';

const styles = {
  ...baseStyles,
  categoriesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '0.5rem',
  },
  favoritosButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.875rem 1rem',
    minHeight: '48px',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.accent}`,
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.accent,
    marginTop: '1.5rem',
  },
  favoritosButtonHover: {
    backgroundColor: colors.accentLight,
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem 1rem',
    color: colors.textMuted,
    fontSize: '0.875rem',
  },
};

export default function LocalizadorGlobal() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [favButtonHovered, setFavButtonHovered] = useState(false);
  const [showAgregarModal, setShowAgregarModal] = useState(false);

  // Estado de ubicación
  const [userLocation, setUserLocation] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  // Cargar ubicación guardada al montar (resuelve nombre si es GPS)
  useEffect(() => {
    resolveStoredLocation().then(location => {
      if (location) {
        setUserLocation(location);
      } else {
        setShowLocationPrompt(true);
      }
    });
  }, []);

  // Manejar cuando el usuario establece su ubicación
  const handleLocationSet = (location) => {
    setUserLocation(location);
    setShowLocationPrompt(false);
  };

  // Manejar cambio de ubicación
  const handleChangeLocation = () => {
    setShowLocationPrompt(true);
  };

  const handleCategoryClick = (categoria) => {
    navigate(`/localizador/resultados/${categoria.id}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Filtrar categorías basado en búsqueda
  const filterCategories = (categories) => {
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(cat =>
      cat.nombre.toLowerCase().includes(query)
    );
  };

  // Categorías filtradas
  const filteredEstablecimientos = useMemo(
    () => filterCategories(categoriasMascota.establecimientos),
    [searchQuery]
  );
  const filteredProveedores = useMemo(
    () => filterCategories(categoriasMascota.proveedores),
    [searchQuery]
  );
  const filteredObjetos = useMemo(
    () => filterCategories(categoriasObjeto.establecimientos),
    [searchQuery]
  );

  const totalResults = filteredEstablecimientos.length + filteredProveedores.length + filteredObjetos.length;
  const hasSearch = searchQuery.trim().length > 0;

  return (
    <div style={styles.container}>
      <div style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 style={styles.title}>Buscar servicios</h1>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <SearchBar
          placeholder="Buscar categorías..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        {/* Indicador de ubicación */}
        <LocationIndicator
          location={userLocation}
          onChangeClick={handleChangeLocation}
        />

        {/* Sin resultados */}
        {hasSearch && totalResults === 0 && (
          <div style={styles.emptyState}>
            No se encontraron categorías para "{searchQuery}"
          </div>
        )}

        {/* Sección Establecimientos */}
        {filteredEstablecimientos.length > 0 && (
          <CollapsibleSection title="Establecimientos" defaultOpen={true}>
            <div style={styles.categoriesGrid}>
              {filteredEstablecimientos.map((cat) => (
                <CategoryButton
                  key={cat.id}
                  categoria={cat}
                  onClick={handleCategoryClick}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Sección Proveedores Independientes */}
        {filteredProveedores.length > 0 && (
          <CollapsibleSection title="Proveedores Independientes" defaultOpen={!hasSearch && filteredEstablecimientos.length === 0}>
            <div style={styles.categoriesGrid}>
              {filteredProveedores.map((cat) => (
                <CategoryButton
                  key={cat.id}
                  categoria={cat}
                  onClick={handleCategoryClick}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Sección Objetos */}
        {filteredObjetos.length > 0 && (
          <CollapsibleSection title="Objetos" defaultOpen={!hasSearch && filteredEstablecimientos.length === 0 && filteredProveedores.length === 0}>
            <div style={styles.categoriesGrid}>
              {filteredObjetos.map((cat) => (
                <CategoryButton
                  key={cat.id}
                  categoria={cat}
                  onClick={handleCategoryClick}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Divisor */}
        <div style={styles.divider} />

        {/* Botón de favoritos */}
        <button
          style={{
            ...styles.favoritosButton,
            ...(favButtonHovered ? styles.favoritosButtonHover : {}),
          }}
          onClick={() => navigate('/localizador/favoritos')}
          onMouseEnter={() => setFavButtonHovered(true)}
          onMouseLeave={() => setFavButtonHovered(false)}
        >
          <span style={{ fontSize: '1.125rem' }}>★</span>
          <span>Ver todos mis favoritos</span>
        </button>

        {/* Botón para agregar lugar */}
        <button
          onClick={() => setShowAgregarModal(true)}
          style={{
            width: '100%',
            padding: '0.875rem',
            marginTop: '0.75rem',
            backgroundColor: colors.surface,
            border: `1px dashed ${colors.border}`,
            borderRadius: '10px',
            fontSize: '0.875rem',
            color: colors.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          <span style={{ fontSize: '1.125rem' }}>+</span>
          Agregar un lugar que conozco
        </button>

        {/* Modal para agregar lugar */}
        <AgregarLugarModal
          isOpen={showAgregarModal}
          onClose={() => setShowAgregarModal(false)}
        />

        {/* Modal de ubicación */}
        <LocationPrompt
          isOpen={showLocationPrompt}
          onLocationSet={handleLocationSet}
          onClose={() => setShowLocationPrompt(false)}
        />
      </div>
    </div>
  );
}
